import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useParams } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TopicNavigator } from "@/components/TopicNavigator";
import { useActiveTopic } from "@/hooks/use-active-topic";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, RefreshCw, Save, Undo2, Shuffle, ArrowUpDown, FolderTree, ShuffleIcon, ChevronUp, ChevronDown } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { TopicSelector } from "@/components/TopicSelector";
import { QuestionCard, type SocratesQuestionData } from "@/components/QuestionCard";
import { type BlueprintDecision } from "@/components/RegenerateQuestionDialog";
import { SortableQuestionItem } from "@/components/SortableQuestionItem";
import { SmartAIChat } from "@/components/SmartAIChat";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useRailAutoCondense } from "@/hooks/use-rail-auto-condense";
import { normalizeAnswerCapitalization } from "@/utils/sortAnswers";
import {
  sortQuestionsByCognitiveLevel,
  randomizeQuestions,
  sortQuestionsByTopic,
  randomizeWithinDifficulty,
  randomizeWithinTopic,
} from "@/utils/sortQuestions";
import { buildQuestionIndexToTaskId } from "@/utils/socratesDuplicate";
import { apiClient } from "@/lib/apiClient";
import { withPreservedScroll } from "@/utils/preserveScroll";
import { AddItemControl, type AddItemLevel, type AddItemRun } from "@/components/AddItemControl";
import {
  generateSingleItem,
  AddItemTimeoutError,
  httpErrorFrom,
  regenerationToast,
} from "@/lib/generationRequests";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { readingLevelToGrade, extractSourceText, type Question, type Test } from "@shared/schema";
import { useChatNarrowWidth } from "@/hooks/use-chat-narrow-width";
import { useChatHalfHeight } from "@/hooks/use-chat-half-height";

// "Edit Items" — dedicated edit surface for an existing saved test.
//
// Mirrors the workflow's ReviewTest functionally (TopicSelector + tabs +
// QuestionCard rendering + regenerate / lock / undo) but lives at
// /tests/:id/edit. Critical differences vs. ReviewTest:
//   - No TestContext involvement. Local state holds the edit buffer so
//     a workflow-in-progress draft is never disturbed.
//   - Explicit Save: edits buffer locally until the user clicks Save,
//     which PATCHes /api/tests/:id with { questions, directions }.
//   - Unsaved-changes guard on back navigation.
//   - No ProgressIndicator, no Smart AI Assistant sidebar (kept lean —
//     can be added later if needed).

// Hard cap on items per test (mirrors BuildBlueprint's MAX_TEST_ITEMS).
const MAX_TEST_ITEMS = 20;
const LEVEL_TO_ENUM: Record<AddItemLevel, Question["level"]> = {
  knowledge: "Knowledge",
  understanding: "Understanding",
  higherOrderThinking: "Higher-Order Thinking",
};

export default function EditItems() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [assistantNarrow] = useChatNarrowWidth();
  const [assistantHalfHeight] = useChatHalfHeight();
  // Lg-and-down sidebar Options Card mirrors the Review and Revise pattern:
  // a condensable "Summary & add item" panel above a fixed Undo footer.
  // Defaults condensed so the chat below has room out of the gate.
  const [condenseOptions, setCondenseOptions] = useState(true);

  // --- Server-loaded record + local edit buffer ----------------------------
  const { data: test, isLoading, error } = useQuery<Test>({
    queryKey: [`/api/tests/${params.id}`],
    enabled: !!params.id,
  });

  // Raw lesson text from the test's materials, sent on regenerate/score calls
  // so SOCRATES runs Content-Appropriate Stem against the real source.
  const sourceText = extractSourceText(test?.config?.materials);

  // Local edit buffer. Seeded from the server record once it loads.
  const [questions, setQuestions] = useState<Question[]>([]);
  // Freshest questions for handlers that run after an async wait (a card
  // Rescore resolving late): the closure-level `questions` is the snapshot at
  // the render that created the handler, not what a concurrent edit/regen
  // landed during the request.
  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  const [directions, setDirections] = useState<string>("");
  const [hasSeeded, setHasSeeded] = useState(false);

  useEffect(() => {
    if (!test || hasSeeded) return;
    setQuestions(test.questions);
    setDirections(test.directions || "");
    setHasSeeded(true);
  }, [test, hasSeeded]);

  // --- Dirty detection -----------------------------------------------------
  // JSON-stringify is good enough at this scale (a few dozen questions).
  const isDirty = useMemo(() => {
    if (!test || !hasSeeded) return false;
    return (
      JSON.stringify(questions) !== JSON.stringify(test.questions) ||
      directions !== (test.directions || "")
    );
  }, [test, hasSeeded, questions, directions]);

  // --- Active topic (scroll-spy over the vertical sections) ----------------
  // Offset clears the sticky top bar (Save/Discard) + the compact selector.
  const topicIds = (test?.blueprint?.topics ?? []).map((t) => t.id);
  const { activeId, scrollToTopic } = useActiveTopic(topicIds, 170);
  const [addingItem, setAddingItem] = useState(false);
  // The topic an in-flight AI generation is for — passed to AddItemControl so
  // the trigger keeps showing it even if that control unmounts/remounts (rail
  // collapse) mid-generation.
  const [addingTopicId, setAddingTopicId] = useState<string | null>(null);
  // Live phase + start time of an in-flight "Add item" generation, so the
  // control can show real progress instead of an opaque spinner (ARC-30 cap 3).
  const [addItemRun, setAddItemRun] = useState<AddItemRun | null>(null);
  // Freshly-added items get a blue border + "New" badge until seen (see
  // QuestionCard's in-view timer, which calls handleItemSeen).
  const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set());
  // Just-regenerated items get the same highlight, with a "Regenerated" badge.
  const [regeneratedItemIds, setRegeneratedItemIds] = useState<Set<string>>(new Set());
  // Per-option regen highlight: question id -> set of regenerated answer indices.
  const [regeneratedOptionIds, setRegeneratedOptionIds] = useState<Map<string, Set<number>>>(new Map());
  // Resolve a question's card-level highlight for QuestionCard (new precedence).
  const highlightFor = (id: string): "new" | "regenerated" | undefined =>
    newItemIds.has(id) ? "new" : regeneratedItemIds.has(id) ? "regenerated" : undefined;
  // toast key -> dismiss() fns. Card/add toasts are keyed by item id; option
  // toasts by `${id}::${index}` so we can dismiss the toast for a specific item
  // OR a specific option. Toasts stack (TOAST_LIMIT) rather than replacing.
  const itemToastDismiss = useRef<Map<string, Array<() => void>>>(new Map());
  const dismissToastKey = (key: string) => {
    itemToastDismiss.current.get(key)?.forEach((d) => d());
    itemToastDismiss.current.delete(key);
  };
  // Dismiss every toast tied to an item: its card toast + all its option toasts.
  const dismissItemToasts = (id: string) => {
    for (const key of Array.from(itemToastDismiss.current.keys())) {
      if (key === id || key.startsWith(`${id}::`)) dismissToastKey(key);
    }
  };
  // Seeing an item (in view ~5s, or clicking its "New"/"Regenerated" badge)
  // clears ALL of its highlights and toasts.
  const handleItemSeen = (id: string) => {
    const dropSet = (prev: Set<string>) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    };
    setNewItemIds(dropSet);
    setRegeneratedItemIds(dropSet);
    setRegeneratedOptionIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    dismissItemToasts(id);
  };
  // Clicking one option's "Regenerated" badge clears just that option's
  // highlight and its toast.
  const handleOptionSeen = (id: string, index: number) => {
    setRegeneratedOptionIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      const set = new Set(next.get(id) ?? []);
      set.delete(index);
      if (set.size === 0) next.delete(id);
      else next.set(id, set);
      return next;
    });
    dismissToastKey(`${id}::${index}`);
  };
  const scrollToItem = (id: string) =>
    document
      .getElementById(`question-card-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  // Persistent highlight toast with a button that jumps to the item. toastKey
  // lets us later dismiss this exact toast (per item or per option).
  const notifyHighlight = (toastKey: string, scrollId: string, title: string, description: string) => {
    const { dismiss } = toast({
      variant: "info",
      title,
      description,
      duration: Infinity,
      action: (
        <ToastAction altText="Scroll to the item" onClick={() => scrollToItem(scrollId)}>
          View item
        </ToastAction>
      ),
    });
    const list = itemToastDismiss.current.get(toastKey) ?? [];
    list.push(dismiss);
    itemToastDismiss.current.set(toastKey, list);
  };
  // Mark a whole item as just-regenerated: card highlight + persistent toast.
  const markRegenerated = (id: string, title: string, description: string) => {
    setRegeneratedItemIds((prev) => new Set(prev).add(id));
    notifyHighlight(id, id, title, description);
  };
  // Mark a single option as just-regenerated: option-level highlight + toast.
  const markRegeneratedOption = (id: string, index: number, title: string, description: string) => {
    setRegeneratedOptionIds((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(id) ?? []);
      set.add(index);
      next.set(id, set);
      return next;
    });
    notifyHighlight(`${id}::${index}`, id, title, description);
  };
  // Collapse the summary + add-item block to give the assistant more room.
  // Condensed by default; Undo stays visible regardless.
  // xl left rail: same condensable card, but expanded by default — the card
  // bounds its own height (summary scrolls internally, Undo stays pinned), so
  // there's no reason to start collapsed when there's room.
  const [railCondensed, setRailCondensed] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const userSetRail = useRef(false);
  // Auto-condense the rail card when expanded it would cover the topics; a
  // manual open is respected until the next resize.
  useRailAutoCondense(railRef, railCondensed, setRailCondensed, userSetRail, questions.length);

  // Top-level mode: edit item content vs. change order & directions.
  const [mode, setMode] = useState<"items" | "order">("items");

  // "Chat with AI about the item" focuses the assistant on that question
  // (same behavior as the create-flow ReviewTest page).
  const [focusedQuestion, setFocusedQuestion] = useState<{
    number: number;
    stem: string;
    answers: string[];
    correctAnswer: number;
    level: string;
  } | null>(null);
  const handleChatWithItem = (question: Question) => {
    setFocusedQuestion({
      number: questions.findIndex((q) => q.id === question.id) + 1,
      stem: question.stem,
      answers: question.answers,
      correctAnswer: question.correctAnswer,
      level: question.level,
    });
  };
  const handleClearFocus = () => setFocusedQuestion(null);

  // Drag-and-drop reordering (Order & Directions tab). Reorders the local
  // questions buffer; Save persists the new order via PATCH.
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setQuestions((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };
  const getTopicName = (topicId: string) =>
    test?.blueprint?.topics.find((t) => t.id === topicId)?.name || "Unknown Topic";

  // --- Regeneration state + history ----------------------------------------
  const [regeneratingQuestionIds, setRegeneratingQuestionIds] = useState<Set<string>>(new Set());
  const [regeneratingAnswers, setRegeneratingAnswers] = useState<Map<string, Set<number>>>(new Map());
  // Newly-added option slots being generated for the first time → "Generating Option…".
  const [addingAnswers, setAddingAnswers] = useState<Map<string, Set<number>>>(new Map());
  const [regenerationHistory, setRegenerationHistory] = useState<Array<{
    questionId: string;
    previousVersion: Question | Question[];
    action: "question" | "answer" | "bulk-question" | "edit" | "delete";
    answerIndex?: number;
    // Original array position, so a deleted item can be re-inserted where it was.
    index?: number;
  }>>([]);

  const markQuestionRegenerating = (id: string, on: boolean) => {
    setRegeneratingQuestionIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const markAnswerRegenerating = (id: string, answerIndex: number, on: boolean) => {
    setRegeneratingAnswers((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(id) ?? []);
      if (on) set.add(answerIndex);
      else set.delete(answerIndex);
      if (set.size === 0) next.delete(id);
      else next.set(id, set);
      return next;
    });
  };
  const markAnswerAdding = (id: string, answerIndex: number, on: boolean) => {
    setAddingAnswers((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(id) ?? []);
      if (on) set.add(answerIndex);
      else set.delete(answerIndex);
      if (set.size === 0) next.delete(id);
      else next.set(id, set);
      return next;
    });
  };

  // --- Socrates badges -----------------------------------------------------
  // The map is the in-render lookup; the canonical copy lives on each
  // question (`socratesScore` / `socratesScoreVoided`) so refreshes don't
  // fall back to the Blacksmith session's stale pre-edit results.
  const [socratesMap, setSocratesMap] = useState<Map<string, SocratesQuestionData>>(new Map());
  const [socratesLoadingIds, setSocratesLoadingIds] = useState<Set<string>>(new Set());
  // Session-level BCE question_index → task_id map, built from the Socrates
  // score response. QuestionCard uses it to resolve a duplicate marker's
  // target index to the item currently on screen (see utils/socratesDuplicate).
  const [socratesIndexToTaskId, setSocratesIndexToTaskId] = useState<Map<number, string>>(new Map());
  const sessionId = questions.find((q) => q.blacksmithSessionId)?.blacksmithSessionId;
  useEffect(() => {
    // Hydrate first from each question's persisted Socrates score — these
    // are the scores we wrote at last edit / re-score, and match the
    // current options.
    const hydrated = new Map<string, SocratesQuestionData>();
    for (const q of questions) {
      if (q.socratesScore) hydrated.set(q.id, q.socratesScore as SocratesQuestionData);
    }
    if (hydrated.size > 0) setSocratesMap(hydrated);

    if (!sessionId) return;

    // Fall back to session-cached results only for questions that aren't
    // already scored and weren't voided by an edit — voided slots must
    // stay blank until a fresh score lands. Updater stays pure; map sync
    // happens after.
    fetch(`/api/socrates-score/${sessionId}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.available || !data.questions) return;
        // Non-positional identity layer for the duplicate advisory: the map
        // survives reordering and edits, so a marker's session index can
        // still be resolved to the item the teacher currently sees.
        setSocratesIndexToTaskId(buildQuestionIndexToTaskId(data.questions));
        let additions: Array<[string, SocratesQuestionData]> = [];
        setQuestions((prevQuestions) => {
          additions = [];
          const matchedTaskIds = new Set<string>();
          const updated = prevQuestions.map((q, qi) => {
            // Prefer stable task_id join so feedback survives reordering.
            // Fall back to position-based question_index for historical
            // records that predate the task_id field.
            const socratesQ = data.questions.find((sq: SocratesQuestionData) => {
              if (sq.task_id && q.taskId) {
                if (sq.task_id === q.taskId) { matchedTaskIds.add(sq.task_id); return true; }
                return false;
              }
              return sq.question_index === qi;
            });
            if (!socratesQ) return q;
            if (q.socratesScoreVoided) return q;
            if (q.socratesScore) return q;
            additions.push([q.id, socratesQ]);
            return { ...q, socratesScore: socratesQ };
          });
          // Dev-time warning: feedback records whose task_id matched nothing
          // in the displayed list — possible session/questions desync.
          if (process.env.NODE_ENV !== 'production') {
            for (const sq of data.questions as SocratesQuestionData[]) {
              if (sq.task_id && !matchedTaskIds.has(sq.task_id)) {
                console.warn(
                  `[Socrates] feedback task_id "${sq.task_id}" (question_index=${sq.question_index}) matched no displayed question — possible desync`,
                );
              }
            }
          }
          return additions.length > 0 ? updated : prevQuestions;
        });
        if (additions.length > 0) {
          setSocratesMap((prev) => {
            const next = new Map(prev);
            for (const [id, score] of additions) next.set(id, score);
            return next;
          });
        }
      })
      .catch((err) => {
        console.warn("[Socrates] Failed to load:", err);
      });
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Edit handlers (operate on local buffer only) ------------------------
  // Manual edits (stem / answer text / correct-answer change) push their own
  // undo entry so they're independently reversible from regenerations. Lock
  // toggles and other non-content changes don't push.
  const handleUpdateQuestion = (updated: Question) => {
    const previous = questions.find((q) => q.id === updated.id);
    let contentChanged = false;
    if (previous) {
      contentChanged =
        previous.stem !== updated.stem ||
        previous.correctAnswer !== updated.correctAnswer ||
        previous.answers.length !== updated.answers.length ||
        previous.answers.some((a, i) => a !== updated.answers[i]);
      if (contentChanged) {
        setRegenerationHistory((prev) => [
          ...prev,
          { questionId: updated.id, previousVersion: { ...previous }, action: "edit" },
        ]);
      }
    }
    // On content change, void any prior Socrates badge — the modal's
    // re-score path will repopulate it via handleSocratesUpdate; inline
    // edits leave it voided until the user re-scores manually.
    const next = contentChanged
      ? { ...updated, socratesScore: undefined, socratesScoreVoided: true }
      : updated;
    setQuestions((prev) => prev.map((q) => (q.id === updated.id ? next : q)));
    if (contentChanged) {
      setSocratesMap((prev) => {
        const m = new Map(prev);
        m.delete(updated.id);
        return m;
      });
    }
  };

  const handleRegenerateQuestion = async (
    questionId: string,
    targetLevel?: Question["level"],
    _blueprintDecision?: BlueprintDecision,
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question) return;
    const questionNumber = questions.findIndex((q) => q.id === questionId) + 1;
    // The saved-test editor never amends the stored blueprint (canAmendBlueprint
    // is false on this surface), so a level choice only affects the item.
    const target = targetLevel ?? question.level;
    setRegenerationHistory((prev) => [
      ...prev,
      { questionId, previousVersion: { ...question }, action: "question" },
    ]);
    const topic = test?.blueprint?.topics.find((t) => t.id === question.topicId);
    markQuestionRegenerating(questionId, true);
    try {
      const response = await apiClient("/api/questions/regenerate", {
        method: "POST",
        body: JSON.stringify({
          question: {
            stem: question.stem,
            answers: question.answers,
            correctAnswer: question.correctAnswer,
            level: target,
            difficulty: question.difficulty,
          },
          topicName: topic?.name || "General",
          grade: readingLevelToGrade(test?.config?.readingLevel),
          standards: question.standards,
          regenerationTarget: "question",
          // Other items on the test for the server's cross-item duplicate
          // check. Excludes the item being regenerated.
          existingItems: questions
            .filter((q) => q.id !== questionId)
            .map((q) => ({ stem: q.stem, answers: q.answers })),
          ...(sourceText ? { sourceText } : {}),
        }),
      });
      if (!response.ok) throw await httpErrorFrom(response);
      const data = await response.json();
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== questionId) return q;
          // Carry optionsPreOrdered through so a BCE-ordered regen keeps
          // BCE's order (SOCRATES feedback letters aligned to it). Waggle
          // never re-sorts: the order handed back is the order displayed.
          const merged = normalizeAnswerCapitalization({
            ...q,
            stem: data.stem,
            answers: data.answers,
            correctAnswer: data.correctAnswer,
            level: target,
            duplicate_warning: data.duplicate_warning,
            // Whole-item regen — the polling IIFE below fills in the
            // new score; mark the slot voided meanwhile so a refresh
            // doesn't keep showing the pre-regen score.
            socratesScore: undefined,
            socratesScoreVoided: true,
            optionsPreOrdered: data.optionsPreOrdered ?? false,
          });
          return merged;
        }),
      );
      // Drop the in-memory badge too while the IIFE polls for the fresh score.
      setSocratesMap((prev) => {
        const next = new Map(prev);
        next.delete(questionId);
        return next;
      });
      markRegenerated(questionId, "Question regenerated", `This item has been regenerated.`);
      // One toast regardless of how many reasons the flag carries.
      if (data.duplicate_warning?.flagged) {
        toast({ title: "Possible duplicate", description: "This item may be similar to an existing item." });
      }
      if (data.blacksmithSessionId) {
        setSocratesLoadingIds(prev => new Set(Array.from(prev).concat(questionId)));
        (async () => {
          let attempts = 0;
          while (attempts < 4) {
            if (attempts > 0) await new Promise(r => setTimeout(r, 3000));
            try {
              const resp = await fetch(`/api/socrates-score/${data.blacksmithSessionId}`);
              const socratesResult = await resp.json();
              if (socratesResult.available && socratesResult.questions?.length > 0) {
                const score = socratesResult.questions[0];
                setSocratesMap(prev => {
                  const next = new Map(prev);
                  next.set(questionId, score);
                  return next;
                });
                // Persist alongside the question so a refresh keeps it.
                setQuestions(prev => prev.map(q =>
                  q.id === questionId ? { ...q, socratesScore: score, socratesScoreVoided: false } : q,
                ));
                break;
              }
            } catch { /* swallow, retry */ }
            attempts++;
          }
          setSocratesLoadingIds(prev => {
            const next = new Set(prev);
            next.delete(questionId);
            return next;
          });
        })();
      }
    } catch (err) {
      console.error("Failed to regenerate question:", err);
      toast({
        ...regenerationToast(err, "Could not regenerate the question. Please try again."),
      });
      setRegenerationHistory((prev) => prev.slice(0, -1));
    } finally {
      markQuestionRegenerating(questionId, false);
    }
  };

  const handleRegenerateAnswer = async (questionId: string, answerIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question) return;
    const questionNumber = questions.findIndex((q) => q.id === questionId) + 1;
    setRegenerationHistory((prev) => [
      ...prev,
      { questionId, previousVersion: { ...question }, action: "answer", answerIndex },
    ]);
    const topic = test?.blueprint?.topics.find((t) => t.id === question.topicId);
    const wasCorrectAnswer = answerIndex === question.correctAnswer;
    markAnswerRegenerating(questionId, answerIndex, true);
    try {
      const response = await apiClient("/api/questions/regenerate", {
        method: "POST",
        body: JSON.stringify({
          question: {
            stem: question.stem,
            answers: question.answers,
            correctAnswer: question.correctAnswer,
            level: question.level,
            difficulty: question.difficulty,
          },
          topicName: topic?.name || "General",
          grade: readingLevelToGrade(test?.config?.readingLevel),
          standards: question.standards,
          regenerationTarget: wasCorrectAnswer ? "correct" : "distractor",
          ...(sourceText ? { sourceText } : {}),
        }),
      });
      if (!response.ok) throw await httpErrorFrom(response);
      const data = await response.json();
      // For a distractor regen, splice in a NEW distractor — never the
      // returned correct answer (the server keeps the same correct answer and
      // only refreshes distractors), preferring one not already present.
      const newAnswerText = wasCorrectAnswer
        ? data.answers[data.correctAnswer]
        : (data.answers.find((a: string, i: number) => i !== data.correctAnswer && !question.answers.includes(a)) ??
           data.answers.find((_: string, i: number) => i !== data.correctAnswer) ??
           data.answers[answerIndex]);
      // Capture the post-regen question so we can auto-score it once the
      // state update commits (setQuestions is async; reading from `questions`
      // right after would still see the old shape).
      let scoredQuestion: Question | undefined;
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== questionId) return q;
          const updatedAnswers = [...q.answers];
          updatedAnswers[answerIndex] = newAnswerText;
          // Void the prior score immediately — its options don't exist on
          // this card anymore, so a refresh that lands before the auto-score
          // (or one that lands after a failed auto-score) must NOT show it.
          // The regenerated option takes the old one's place — the set is
          // never re-sorted, so the regenerated index/letter stays correct.
          const next = normalizeAnswerCapitalization({
            ...q,
            answers: updatedAnswers,
            socratesScore: undefined,
            socratesScoreVoided: true,
          });
          scoredQuestion = next;
          return next;
        }),
      );
      // Drop the in-memory entry too so the card doesn't keep showing the
      // pre-edit badge while the new score is in flight.
      setSocratesMap((prev) => {
        const next = new Map(prev);
        next.delete(questionId);
        return next;
      });
      markRegeneratedOption(
        questionId,
        answerIndex,
        "Option regenerated",
        `Option ${String.fromCharCode(65 + answerIndex)} has been regenerated.`,
      );
      // Auto-re-score so the new options land with a fresh Socrates result
      // instead of an empty slot.
      if (scoredQuestion) void scoreAddedItem(questionId, scoredQuestion);
    } catch (err) {
      console.error("Failed to regenerate answer:", err);
      toast({
        ...regenerationToast(err, "Could not regenerate the answer. Please try again."),
      });
      setRegenerationHistory((prev) => prev.slice(0, -1));
    } finally {
      markAnswerRegenerating(questionId, answerIndex, false);
    }
  };

  const handleDeleteQuestion = (questionId: string) => {
    const index = questions.findIndex((q) => q.id === questionId);
    if (index === -1) return;
    const removed = questions[index];
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
    // Record on the undo stack so the deletion can be reversed.
    setRegenerationHistory((prev) => [
      ...prev,
      { questionId, previousVersion: removed, action: "delete", index },
    ]);
    toast({ title: "Item deleted", description: "Removed from the test — use Undo to restore it." });
  };

  // Insert a question right after the last existing item of its topic so it
  // joins that topic's section and the running item numbers stay sensible.
  const insertQuestionInTopic = (newQ: Question) =>
    setQuestions((prev) => {
      const idxs = prev.map((q, i) => (q.topicId === newQ.topicId ? i : -1)).filter((i) => i >= 0);
      const at = idxs.length ? idxs[idxs.length - 1] + 1 : prev.length;
      const copy = [...prev];
      copy.splice(at, 0, newQ);
      return copy;
    });

  // Run Socrates validation on a freshly added AI item so it lands with a
  // score like the rest of the test. Fire-and-forget: the card shows
  // "Scoring…" meanwhile. Blank items have nothing to score yet.
  const scoreAddedItem = async (id: string, q: Question) => {
    if (!q.answers || q.answers.length < 2 || q.answers.length > 6) return;
    if (!q.stem?.trim()) return;
    // Each state change can grow/shrink the (possibly off-screen) new card, so
    // preserve scroll around them too — adding an item should never move the view.
    withPreservedScroll(() => setSocratesLoadingIds((prev) => new Set(prev).add(id)));
    try {
      const resp = await apiClient("/api/questions/score", {
        method: "POST",
        body: JSON.stringify({
          stem: q.stem,
          answers: q.answers,
          correctAnswer: q.correctAnswer,
          targetGrade: readingLevelToGrade(test?.config?.readingLevel),
          ...(sourceText ? { sourceText } : {}),
        }),
      });
      if (resp.ok) {
        const score = await resp.json();
        withPreservedScroll(() => {
          setSocratesMap((prev) => new Map(prev).set(id, score));
          // Persist alongside the question so a refresh keeps the fresh
          // score (rather than falling back to the session's pre-edit data).
          // The isolated endpoint scores q.answers verbatim, so this score's
          // letters already match the displayed order.
          setQuestions((prev) =>
            prev.map((q) =>
              q.id === id
                ? { ...q, socratesScore: score, socratesScoreVoided: false }
                : q,
            ),
          );
        });
      }
    } catch {
      /* leave unscored — the user can still score it from Edit */
    } finally {
      withPreservedScroll(() =>
        setSocratesLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        }),
      );
    }
  };

  const handleAddItem = async ({ topicId, level, mode }: { topicId: string; level: AddItemLevel; mode: "ai" | "blank" }) => {
    if (questions.length >= MAX_TEST_ITEMS) {
      toast({ title: "Item limit reached", description: `Tests are capped at ${MAX_TEST_ITEMS} items.`, variant: "destructive" });
      return;
    }
    const topic = test?.blueprint?.topics.find((t) => t.id === topicId);
    if (!topic) return;
    const newId = `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    if (mode === "blank") {
      // Insert without sliding the user's view around (no auto-scroll either).
      withPreservedScroll(() => {
        insertQuestionInTopic({
          id: newId, topicId, level: LEVEL_TO_ENUM[level],
          stem: "", answers: ["", "", "", ""], correctAnswer: 0,
          locked: false, stemLocked: false, answersLocked: false,
        });
        setNewItemIds((prev) => new Set(prev).add(newId));
      });
      notifyHighlight(newId, newId, "Blank item added", `Added to ${topic.name}. Use Edit to fill it in.`);
      return;
    }

    setAddingItem(true);
    setAddingTopicId(topicId);
    // ARC-30 cap 3: this used to be one POST that held the connection open for
    // up to 8 minutes with nothing to show for it. generateSingleItem() submits,
    // polls the real BCE session and retrieves — same start/status/finish shape
    // batch generation uses — and reports each phase so the control can show
    // what is happening and for how long.
    setAddItemRun({ startedAt: Date.now(), phase: "submitting", degraded: false });
    try {
      const config = test?.config;
      if (!config) throw new Error("no test config available");
      const { question, duplicate_warning } = await generateSingleItem(
        {
          topic: { id: topic.id, name: topic.name, standards: topic.standards },
          level,
          config,
          // The generated item is not among these yet — every current item on
          // the test goes to the server's cross-item duplication check.
          existingItems: questions.map((q) => ({ stem: q.stem, answers: q.answers })),
        },
        {
          token: localStorage.getItem("auth_token"),
          onProgress: ({ phase, degraded }) =>
            setAddItemRun((prev) => (prev ? { ...prev, phase, degraded } : prev)),
        },
      );
      const added = { ...question, id: newId, topicId, duplicate_warning } as Question;
      // Insert without sliding the user's view around — a generated item lands
      // asynchronously and the blue highlight + persistent toast's "View item"
      // button let them go to it when they choose.
      withPreservedScroll(() => {
        insertQuestionInTopic(added);
        setNewItemIds((prev) => new Set(prev).add(newId));
      });
      notifyHighlight(newId, newId, "Item generated", `Added to ${topic.name}.`);
      // One toast regardless of how many reasons the flag carries.
      if (duplicate_warning?.flagged) {
        toast({ title: "Possible duplicate", description: "This item may be similar to an existing item." });
      }
      void scoreAddedItem(newId, added);
    } catch (err) {
      console.error("Add item failed:", err);
      // "We stopped waiting" is not "it failed" — the item is still being
      // generated server-side, and telling the user it failed is what makes
      // them start over and pay for it twice (ARC-30).
      const stillRunning = err instanceof AddItemTimeoutError;
      toast({
        title: stillRunning ? "Still generating" : "Couldn't generate item",
        description: stillRunning
          ? "This item is taking longer than usual and is still being written. Try adding it again in a minute, or add a blank item meanwhile."
          : "Blacksmith may be busy — try again, or add a blank item.",
        variant: stillRunning ? undefined : "destructive",
      });
    } finally {
      setAddingItem(false);
      setAddingTopicId(null);
      setAddItemRun(null);
    }
  };

  // Append a new answer option and fill it via the regenerate-option path.
  const handleAddOption = async (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question || question.answers.length >= 6) return;

    const newIndex = question.answers.length;
    const questionNumber = questions.findIndex((q) => q.id === questionId) + 1;
    setQuestions((prev) => prev.map((q) => (q.id === questionId ? { ...q, answers: [...q.answers, ""] } : q)));
    markAnswerAdding(questionId, newIndex, true);

    const topic = test?.blueprint?.topics.find((t) => t.id === question.topicId);
    try {
      const response = await apiClient("/api/questions/regenerate", {
        method: "POST",
        body: JSON.stringify({
          question: {
            stem: question.stem,
            answers: question.answers,
            correctAnswer: question.correctAnswer,
            level: question.level,
            difficulty: question.difficulty,
          },
          topicName: topic?.name || "General",
          grade: readingLevelToGrade(test?.config?.readingLevel),
          standards: question.standards,
          regenerationTarget: "distractor",
          // Other items on the test for the server's cross-item duplicate
          // check. Excludes the item getting the new option.
          existingItems: questions
            .filter((q) => q.id !== questionId)
            .map((q) => ({ stem: q.stem, answers: q.answers })),
          ...(sourceText ? { sourceText } : {}),
        }),
      });
      if (!response.ok) throw await httpErrorFrom(response);
      const data = await response.json();
      // A fresh distractor for the new slot: not the returned correct answer,
      // and not already one of this question's options.
      const candidate: string =
        (Array.isArray(data.answers)
          ? data.answers.find((a: string, i: number) => i !== data.correctAnswer && !question.answers.includes(a))
          : undefined) ??
        (Array.isArray(data.answers)
          ? data.answers.find((_: string, i: number) => i !== data.correctAnswer)
          : undefined) ??
        "";
      let scoredQuestion: Question | undefined;
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== questionId) return q;
          const updatedAnswers = [...q.answers];
          updatedAnswers[newIndex] = candidate;
          // The new option keeps the slot it was added to — the set is never
          // re-sorted, so the added option's letter stays correct.
          const next = normalizeAnswerCapitalization({
            ...q,
            answers: updatedAnswers,
            duplicate_warning: data.duplicate_warning,
            // Void the prior score — options changed, the auto-score below
            // will replace this with a fresh result.
            socratesScore: undefined,
            socratesScoreVoided: true,
          });
          scoredQuestion = next;
          return next;
        }),
      );
      setSocratesMap((prev) => {
        const next = new Map(prev);
        next.delete(questionId);
        return next;
      });
      toast({
        variant: "info",
        title: "Option added",
        description: `Option ${String.fromCharCode(65 + newIndex)} has been generated.`,
      });
      if (data.duplicate_warning?.flagged) {
        toast({ title: "Possible duplicate", description: "This item may be similar to an existing item." });
      }
      // Auto-re-score against the new option set.
      if (scoredQuestion) void scoreAddedItem(questionId, scoredQuestion);
    } catch (err) {
      console.error("Failed to add option:", err);
      setQuestions((prev) =>
        prev.map((q) => (q.id === questionId ? { ...q, answers: q.answers.filter((_, i) => i !== newIndex) } : q)),
      );
      toast({ title: "Couldn't add option", description: "Please try again." });
    } finally {
      markAnswerAdding(questionId, newIndex, false);
    }
  };

  const handleRegenerateTopicUnlocked = async (topicId: string) => {
    const unlocked = questions.filter((q) => q.topicId === topicId && !q.locked);
    if (unlocked.length === 0) {
      toast({
        title: "No Unlocked Items",
        description: "All questions in this topic are locked",
      });
      return;
    }
    setRegenerationHistory((prev) => [
      ...prev,
      {
        questionId: `bulk-regenerate-${topicId}`,
        previousVersion: unlocked.map((q) => ({ ...q })),
        action: "bulk-question",
      },
    ]);
    const topic = test?.blueprint?.topics.find((t) => t.id === topicId);
    const topicName = topic?.name || "Unknown Topic";
    unlocked.forEach((q) => markQuestionRegenerating(q.id, true));
    try {
      const promises = unlocked.map(async (question) => {
        const response = await apiClient("/api/questions/regenerate", {
          method: "POST",
          body: JSON.stringify({
            question: {
              stem: question.stem,
              answers: question.answers,
              correctAnswer: question.correctAnswer,
              level: question.level,
              difficulty: question.difficulty,
            },
            topicName,
            grade: readingLevelToGrade(test?.config?.readingLevel),
            standards: question.standards,
            ...(sourceText ? { sourceText } : {}),
          }),
        });
        if (!response.ok) throw await httpErrorFrom(response);
        const data = await response.json();
        return { id: question.id, ...data };
      });
      const results = await Promise.all(promises);
      const regeneratedIds = new Set(results.map((r) => r.id));
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.topicId !== topicId || q.locked) return q;
          const regenerated = results.find((r) => r.id === q.id);
          if (!regenerated) return q;
          // Keep BCE's order when the regen was BCE-ordered; otherwise the
          // stored order is never re-sorted.
          const merged = normalizeAnswerCapitalization({
            ...q,
            stem: regenerated.stem,
            answers: regenerated.answers,
            correctAnswer: regenerated.correctAnswer,
            // Bulk regen — score on each item now refers to its
            // pre-regen options. Void; the user can re-score manually
            // (bulk path doesn't auto-score yet).
            socratesScore: undefined,
            socratesScoreVoided: true,
            optionsPreOrdered: regenerated.optionsPreOrdered ?? false,
          });
          return merged;
        }),
      );
      setSocratesMap((prev) => {
        const next = new Map(prev);
        for (const id of regeneratedIds) next.delete(id);
        return next;
      });
      toast({
        variant: "info",
        title: "Topic Items Regenerated",
        description: `Regenerated ${unlocked.length} unlocked questions in ${topicName}`,
      });
    } catch (err) {
      console.error("Failed to regenerate topic questions:", err);
      toast({
        ...regenerationToast(err, "Some questions could not be regenerated. Please try again."),
      });
      setRegenerationHistory((prev) => prev.slice(0, -1));
    } finally {
      unlocked.forEach((q) => markQuestionRegenerating(q.id, false));
    }
  };

  const handleUndoRegenerate = () => {
    if (regenerationHistory.length === 0) return;
    const last = regenerationHistory[regenerationHistory.length - 1];
    if (last.action === "bulk-question") {
      const previousQuestions = last.previousVersion as Question[];
      setQuestions((prev) =>
        prev.map((q) => previousQuestions.find((pq) => pq.id === q.id) || q),
      );
      toast({
        title: "Bulk Regeneration Undone",
        description: `Reverted ${previousQuestions.length} questions`,
      });
    } else if (last.action === "edit") {
      setQuestions((prev) =>
        prev.map((q) => (q.id === last.questionId ? (last.previousVersion as Question) : q)),
      );
      toast({
        title: "Edit Undone",
        description: "Reverted your last manual edit",
      });
    } else if (last.action === "delete") {
      // Re-insert the deleted item at its original position.
      const restored = last.previousVersion as Question;
      setQuestions((prev) => {
        const at = Math.min(last.index ?? prev.length, prev.length);
        const next = [...prev];
        next.splice(at, 0, restored);
        return next;
      });
      toast({
        title: "Deletion Undone",
        description: "Restored the deleted item.",
      });
    } else {
      setQuestions((prev) =>
        prev.map((q) => (q.id === last.questionId ? (last.previousVersion as Question) : q)),
      );
      const actionType =
        last.action === "question"
          ? "question"
          : `answer option ${String.fromCharCode(65 + (last.answerIndex || 0))}`;
      toast({
        title: "Regeneration Undone",
        description: `Reverted ${actionType} regeneration`,
      });
    }
    setRegenerationHistory((prev) => prev.slice(0, -1));
  };

  const handleToggleLockAll = (checked: boolean) => {
    setQuestions((prev) => prev.map((q) => ({ ...q, locked: checked })));
  };

  const handleSocratesUpdate = (questionId: string, score: SocratesQuestionData) => {
    setSocratesMap(prev => {
      const next = new Map(prev);
      next.set(questionId, score);
      return next;
    });
    // Mirror onto the question so the score survives a refresh.
    setQuestions(prev => prev.map(q =>
      q.id === questionId ? { ...q, socratesScore: score, socratesScoreVoided: false } : q,
    ));
  };

  // Card Rescore path. The isolated /api/questions/score endpoint scores the
  // displayed answers verbatim, so its letters are display-aligned and the
  // pre-sort→display map must never accompany them. Merge score + cleared map
  // onto the CURRENT question under a functional updater (the scoreAddedItem
  // pattern) so a newer edit or regeneration that landed while the rescore
  // request was in flight is never reverted by the card's pre-request
  // snapshot. The merge applies ONLY if the current question still matches
  // the content that was POSTed: a stale response for content that was since
  // superseded must not overwrite the newer score with one computed against
  // the old answers.
  const handleSocratesRescore = (
    questionId: string,
    score: SocratesQuestionData,
    submitted: { stem?: string; answers?: string[]; correctAnswer?: number },
  ) => {
    const current = questionsRef.current.find(q => q.id === questionId);
    const matches =
      !!current &&
      current.stem === submitted.stem &&
      current.correctAnswer === submitted.correctAnswer &&
      (submitted.answers?.length ?? -1) === current.answers.length &&
      (submitted.answers ?? []).every((a, i) => a === current.answers[i]);
    // Content changed while the request was in flight — the response was
    // computed against content that is no longer the current question, so
    // discard it: never overwrite a score for the newer content with one for
    // the old answers.
    if (!matches) return;
    setSocratesMap(prev => {
      const next = new Map(prev);
      next.set(questionId, score);
      return next;
    });
    setQuestions(prev => prev.map(q =>
      q.id === questionId
        ? { ...q, socratesScore: score, socratesScoreVoided: false }
        : q,
    ));
  };

  // --- Save / Discard / Navigation guard -----------------------------------
  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/tests/${params.id}`, {
        questions,
        directions,
      });
      return res.json() as Promise<Test>;
    },
    onSuccess: (updated) => {
      // Anchor the dirty baseline to the freshly-saved record by updating
      // the cache; isDirty will recompute against the new data.
      queryClient.setQueryData([`/api/tests/${params.id}`], updated);
      queryClient.invalidateQueries({ queryKey: ["/api/tests"] });
      toast({ title: "Saved", description: "Your changes have been saved." });
    },
    onError: () => {
      toast({
        title: "Save failed",
        description: "Could not save your changes. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDiscard = () => {
    if (!test) return;
    setQuestions(test.questions);
    setDirections(test.directions || "");
    setRegenerationHistory([]);
  };

  // Unsaved-changes guard for back-link clicks.
  const [pendingNav, setPendingNav] = useState<string | null>(null);
  const requestNav = (path: string) => {
    if (isDirty) setPendingNav(path);
    else setLocation(path);
  };

  // Browser refresh / tab close guard.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // --- Render --------------------------------------------------------------
  if (isLoading || !hasSeeded) {
    return (
      <div className="min-h-screen bg-background">
        <Header pageTitle="Edit Test" />
        <main className="container mx-auto px-6 py-12 max-w-7xl">
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !test) {
    return (
      <div className="min-h-screen bg-background">
        <Header pageTitle="Edit Test" />
        <main className="container mx-auto px-6 py-12 max-w-4xl">
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-muted-foreground">
                We couldn&rsquo;t load this test for editing.
              </p>
              <Button variant="outline" onClick={() => setLocation("/dashboard")}>
                Back to My Tests
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const blueprint = test.blueprint;
  const topics = blueprint?.topics ?? [];
  const questionsByTopic = topics.map((topic) => ({
    topic,
    questions: questions.filter((q) => q.topicId === topic.id),
  }));
  const lockAll = questions.length > 0 && questions.every((q) => q.locked);
  // Per-topic "Regenerate unlocked items" is hidden for now; the handler/logic
  // are kept — flip to true to bring the button back. ARC-473 flipped it: the
  // acceptance requires E2E coverage of the topic-regeneration handler.
  const SHOW_TOPIC_REGEN = true;
  const totalKnowledge = questions.filter((q) => q.level === "Knowledge").length;
  const totalUnderstanding = questions.filter((q) => q.level === "Understanding").length;
  const totalHigherOrder = questions.filter((q) => q.level === "Higher-Order Thinking").length;

  // Options/Summary box, reused in the left rail (xl) and the right rail
  // (md / stacked). Order: Summary → Add item → Undo.
  const editSummaryBlock = (
    <div>
      <h3 className="font-semibold mb-3">Summary</h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Total questions</span>
          <span className="font-semibold tabular-nums">{questions.length}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Knowledge</span>
          <span className="font-semibold">{totalKnowledge}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Understanding</span>
          <span className="font-semibold">{totalUnderstanding}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Application+</span>
          <span className="font-semibold">{totalHigherOrder}</span>
        </div>
      </div>
    </div>
  );
  const editAddItemBlock = (
    <AddItemControl
      topics={topics}
      total={questions.length}
      cap={MAX_TEST_ITEMS}
      busy={addingItem}
      generatingTopicId={addingTopicId}
      run={addItemRun}
      onAdd={handleAddItem}
    />
  );
  const editUndoBlock = (
    <Button
      variant="secondary"
      className="w-full"
      onClick={handleUndoRegenerate}
      disabled={regenerationHistory.length === 0}
      data-testid="button-undo-regenerate"
    >
      <Undo2 className="w-4 h-4 mr-2" />
      Undo last
    </Button>
  );


  return (
    <div className="min-h-screen bg-background">
      <Header pageTitle="Edit Test" />

      <main className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Narrow-view sticky bar: stays pinned to the top of the
            viewport so teachers don't lose track of having to save while
            scrolling through items. Negative horizontal margin + matching
            padding extends the bar background full-width across the
            container's px-6. Above md, the sidebar carries Save / Discard
            so the bar collapses back to a normal in-flow row with just
            the back button. */}
        {/* Shared sticky top bar (both tabs): Back + tab toggle on the left,
            Save / Discard on the right, always visible as the page scrolls. */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6 sticky top-0 z-30 -mx-6 px-6 py-3 bg-background border-b">
          <div className="flex items-center gap-4 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => requestNav(`/tests/${test.id}`)}
              className="-ml-2"
              data-testid="button-back-to-preview"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to preview
            </Button>

            {/* Items / Order & Directions tabs */}
            <div className="inline-flex rounded-lg border p-1">
              <Button
                variant={mode === "items" ? "secondary" : "ghost"}
                onClick={() => setMode("items")}
                data-testid="button-mode-items"
              >
                Items
              </Button>
              <Button
                variant={mode === "order" ? "secondary" : "ghost"}
                onClick={() => setMode("order")}
                data-testid="button-mode-order"
              >
                Order &amp; Directions
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDiscard}
              disabled={!isDirty || saveMutation.isPending}
              data-testid="button-discard-narrow"
            >
              Discard
            </Button>
            <Button
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={!isDirty || saveMutation.isPending}
              data-testid="button-save-changes-narrow"
            >
              {saveMutation.isPending ? "Saving…" : isDirty ? "Save" : "Saved"} <Save className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* md:grid (768px) instead of lg (1024px) — the desktop layout
            kicks in earlier since there's enough width to spare. Below
            md, the options panel stacks below the items rather than
            disappearing. */}
        <div className={`md:grid md:gap-6 ${mode === "items"
          // Match the Review and Revise column widths exactly (288 narrow
          // / 384 wide) so the Summary + add-item card has the same room
          // it does there. The xl rail stays at 210px (TopicNavigator
          // surface, unchanged from EditItems' own design).
          ? assistantNarrow
            ? "md:grid-cols-[minmax(0,1fr)_288px] xl:grid-cols-[210px_minmax(0,1fr)_288px]"
            : "md:grid-cols-[minmax(0,1fr)_384px] xl:grid-cols-[210px_minmax(0,1fr)_384px]"
          : assistantNarrow
            ? "md:grid-cols-[minmax(0,1fr)_240px]"
            : "md:grid-cols-[minmax(0,1fr)_320px]"}`}>
          {/* Left rail (≥xl, items mode): topic navigator at the top,
              options/summary aligned to the bottom of the viewport. */}
          {mode === "items" && (
            <div ref={railRef} className="hidden xl:flex xl:flex-col sticky top-24 h-[calc(100vh-7rem)]">
              <TopicNavigator
                className="flex-1 min-h-0"
                topics={topics}
                activeId={activeId}
                onSelect={scrollToTopic}
                counts={Object.fromEntries(questionsByTopic.map(({ topic, questions: qs }) => [topic.id, qs.length]))}
              />
              {/* Options pinned to the bottom of the rail, in the same condensable
                  card as the medium view: Summary + add item are hidden by default
                  and only appear when the toggle is clicked, so they never push
                  Undo off the bottom on shorter screens. The navigator above
                  flexes and scrolls so a long topic list can't push this card
                  out of frame. */}
              <Card className="mt-4 shrink-0 flex flex-col min-h-0 overflow-hidden">
                <CardContent className="pt-4 pb-4 flex flex-col gap-3 min-h-0 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      userSetRail.current = true;
                      setRailCondensed((c) => !c);
                    }}
                    className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                    data-testid="button-condense-options-rail"
                  >
                    <span>Summary &amp; add item</span>
                    {railCondensed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </button>
                  {/* Keep a generating indicator visible when the card is
                      collapsed so the user knows an item is still cooking. */}
                  {railCondensed && addingItem && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Generating an item…
                    </div>
                  )}
                  {!railCondensed && (
                    <div className="space-y-5 min-h-0 overflow-y-auto">
                      {editSummaryBlock}
                      <div className="pt-4 border-t border-border">{editAddItemBlock}</div>
                    </div>
                  )}
                  <div className="shrink-0 pt-3 border-t border-border">
                    {editUndoBlock}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <div className="min-w-0">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground mb-1">{test.name}</h1>
              <p className="text-sm text-muted-foreground">
                Edit items, or reorder them and adjust the directions. Save when you&rsquo;re done.
              </p>
            </div>

            {mode === "items" && (
            <>
            <p className="text-xs text-muted-foreground" data-testid="ai-item-note">
              AI-generated items can contain mistakes. Always confirm the correct answer.
            </p>
            {/* Compact topic nav for < xl — sticks below the top bar. */}
            <div className="xl:hidden sticky top-14 z-20 bg-background">
              <TopicSelector
                topics={topics}
                value={activeId}
                onChange={scrollToTopic}
              />
            </div>

            {/* All items in one vertical list, segmented by big topic headers. */}
            <div className="space-y-12 mt-6">
              {questionsByTopic.map(({ topic, questions: topicQuestions }) => (
                <section
                  key={topic.id}
                  id={`topic-${topic.id}`}
                  className="scroll-mt-32 space-y-6"
                  data-testid={`topic-section-${topic.id}`}
                >
                  <div className="flex justify-between items-end gap-3 border-b border-border pb-2">
                    <h2 className="text-2xl font-bold text-foreground flex-1 min-w-0 wrap-break-word">{topic.name}</h2>
                    {SHOW_TOPIC_REGEN && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegenerateTopicUnlocked(topic.id)}
                        disabled={topicQuestions.every((q) => q.locked)}
                        data-testid={`button-regenerate-topic-${topic.id}`}
                      >
                        Regenerate unlocked items for this topic <RefreshCw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {topicQuestions.map((question) => (
                    <QuestionCard
                      key={question.id}
                      question={question}
                      // Global position across the whole test.
                      number={questions.indexOf(question) + 1}
                      onUpdate={handleUpdateQuestion}
                      onRegenerate={handleRegenerateQuestion}
                      onRegenerateAnswer={handleRegenerateAnswer}
                      onDelete={handleDeleteQuestion}
                      onAddOption={handleAddOption}
                      topicName={topic.name}
                      readingLevel={test?.config?.readingLevel}
                      sourceText={sourceText}
                      onChat={handleChatWithItem}
                      showLock={false}
                      // The saved-test editor saves items only; it never amends
                      // the stored blueprint, so no Update/Keep prompt here.
                      canAmendBlueprint={false}
                      socratesData={socratesMap.get(question.id)}
                      socratesLoading={socratesLoadingIds.has(question.id)}
                      onSocratesUpdate={handleSocratesUpdate}
                      onSocratesRescore={handleSocratesRescore}
                      questionIndexToTaskId={socratesIndexToTaskId}
                      allQuestions={questions}
                      isRegeneratingQuestion={regeneratingQuestionIds.has(question.id)}
                      regeneratingAnswerIndices={Array.from(regeneratingAnswers.get(question.id) ?? [])}
                      addingAnswerIndices={Array.from(addingAnswers.get(question.id) ?? [])}
                      highlight={highlightFor(question.id)}
                      regeneratedOptions={Array.from(regeneratedOptionIds.get(question.id) ?? [])}
                      onSeen={handleItemSeen}
                      onClearOption={handleOptionSeen}
                    />
                  ))}
                </section>
              ))}
            </div>
            </>
            )}

            {mode === "order" && (
              <div className="space-y-8">
                {/* Directions */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-3">Test Directions</h3>
                    <Textarea
                      value={directions}
                      onChange={(e) => setDirections(e.target.value)}
                      className="min-h-[160px] text-sm"
                      placeholder="Enter test directions here..."
                      data-testid="textarea-edit-directions"
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      These directions appear at the beginning of the test. Edit as needed.
                    </p>
                  </CardContent>
                </Card>

                {/* Ordering Options have moved to the sidebar card (matches
                    the Finalize Test layout), leaving the directions and
                    drag-reorder list in this main column. */}

                {/* Drag-reorder list */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={questions.map((q) => q.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-1">
                      {questions.map((question, index) => (
                        <SortableQuestionItem
                          key={question.id}
                          question={question}
                          index={index}
                          topicName={getTopicName(question.topicId)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>

          {/* Items mode sidebar — mirrors the Review and Revise layout:
              a single sticky flex column whose total height toggles
              between full (calc(100vh-7rem)) and 70vh via the chat's
              height chevron. Card sits on top with max-h-[55%]; the
              chat fills the remaining space as flex-1. Sticky offset is
              top-24 to clear EditItems' own sticky Save/Discard bar
              (Review and Revise uses top-6 because it has no such bar).
              The only intentional difference from Review and Revise:
              editUndoBlock is just Undo last — there is no Finalize
              button on Edit Test (Save / Discard live in the top bar). */}
          {mode === "items" && (
            <div
              className={`mt-6 md:mt-0 md:flex md:flex-col md:gap-4 md:sticky md:top-24 ${assistantHalfHeight ? "md:h-[70vh]" : "md:h-[calc(100vh-7rem)]"}`}
            >
              <Card className="xl:hidden md:flex md:flex-col md:min-h-0 md:max-h-[55%]">
                <CardContent className="pt-4 pb-4 flex flex-col gap-3 min-h-0 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setCondenseOptions((c) => !c)}
                    className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                    data-testid="button-condense-options"
                  >
                    <span>Summary &amp; add item</span>
                    {condenseOptions ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </button>
                  {!condenseOptions && (
                    <div className="grid grid-cols-2 gap-4 min-h-0 overflow-y-auto">
                      {editSummaryBlock}
                      {editAddItemBlock}
                    </div>
                  )}
                  <div className="shrink-0 pt-3 border-t border-border">
                    {editUndoBlock}
                  </div>
                </CardContent>
              </Card>

              <div className="md:flex-1 md:min-h-0">
                <SmartAIChat
                  className="h-full"
                  context="review-test"
                  focusedQuestion={focusedQuestion}
                  onClearFocus={handleClearFocus}
                  toggleMode="half"
                />
              </div>
            </div>
          )}

          {/* Order & Directions sidebar — mirrors the Finalize Test
              layout. The chat is a fixed ~456px tall pane (no chevron,
              no width toggle); the Ordering Options card sits beneath
              it with viewport-aware sticky:
                • Tall (≥900px): chat is sticky top-24 (clears the page
                  top bar), card is sticky just below it with a 24px gap.
                  Both pin to the top of the viewport and follow scroll.
                • Short (<900px): chat is in normal flow (scrolls off
                  with the page). Card is sticky top-24 — initially below
                  the fold, scrolls up into view, then pins so it stays
                  visible while the user scrolls further down. */}
          {mode === "order" && (
            <div className="mt-6 md:mt-0 space-y-6">
              <div className="h-[calc(480px-1.5rem)] [@media(min-height:900px)]:sticky [@media(min-height:900px)]:top-24">
                <SmartAIChat
                  context="review-test"
                  focusedQuestion={focusedQuestion}
                  onClearFocus={handleClearFocus}
                  showWidthToggle={false}
                  isCollapsible={false}
                  className="h-full"
                />
              </div>

              <Card className="sticky top-24 [@media(min-height:900px)]:top-[calc(480px+6rem)]">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-4">Ordering Options</h3>
                  {/* 2-up grid: Randomize spans the top row; the "Sort
                      by …" and "Randomize within …" pairs sit beneath it. */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="col-span-2 w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                      onClick={() => setQuestions((qs) => randomizeQuestions(qs))}
                      data-testid="button-edit-randomize"
                    >
                      Randomize <Shuffle className="w-4 h-4 shrink-0" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                      onClick={() => setQuestions((qs) => sortQuestionsByCognitiveLevel(qs))}
                      data-testid="button-edit-sort-difficulty"
                    >
                      Sort by Difficulty <ArrowUpDown className="w-4 h-4 shrink-0" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                      onClick={() => setQuestions((qs) => sortQuestionsByTopic(qs, test.blueprint))}
                      data-testid="button-edit-sort-topic"
                    >
                      Sort by Topic <FolderTree className="w-4 h-4 shrink-0" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                      onClick={() => setQuestions((qs) => randomizeWithinDifficulty(qs))}
                      data-testid="button-edit-randomize-difficulty"
                    >
                      Randomize within Difficulty <ShuffleIcon className="w-4 h-4 shrink-0" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                      onClick={() => setQuestions((qs) => randomizeWithinTopic(qs, test.blueprint))}
                      data-testid="button-edit-randomize-topic"
                    >
                      Randomize within Topic <ShuffleIcon className="w-4 h-4 shrink-0" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Unsaved-changes guard */}
      <AlertDialog
        open={!!pendingNav}
        onOpenChange={(o) => !o && setPendingNav(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved edits. What would you like to do?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => setPendingNav(null)}>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                if (pendingNav) {
                  const dest = pendingNav;
                  setPendingNav(null);
                  setLocation(dest);
                }
              }}
            >
              Discard &amp; leave
            </Button>
            <AlertDialogAction
              onClick={async () => {
                const dest = pendingNav;
                setPendingNav(null);
                try {
                  await saveMutation.mutateAsync();
                  if (dest) setLocation(dest);
                } catch {
                  /* error toast already shown by mutation */
                }
              }}
              disabled={saveMutation.isPending}
            >
              Save &amp; leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
